import React, { useEffect, useRef } from 'react';
import { useTest } from '../contexts/TestContext';
import SoundUtils from '../utils/SoundUtils';
import { gsap } from 'gsap';

const ResultPage = () => {
  const { result } = useTest();
  const contentRef = useRef(null);
  const backgroundRef = useRef(null);
  const backgroundImageRef = useRef(null);
  const buttonsRef = useRef(null);

  // 結果圖片映射 - 確保路徑正確
  const resultImages = {
    "身體": "/assets/images/result-body.png",
    "時間": "/assets/images/result-time.png",
    "禮物": "/assets/images/result-gift.png",
    "服務": "/assets/images/result-service.png",
    "讚美": "/assets/images/result-praise.png"
  };

  // 下載用的高解析度結果圖片映射 - 不同的路徑
  const downloadResultImages = {
    "身體": "/assets/images/download/result-body-hd.jpg",
    "時間": "/assets/images/download/result-time-hd.jpg",
    "禮物": "/assets/images/download/result-gift-hd.jpg",
    "服務": "/assets/images/download/result-service-hd.jpg",
    "讚美": "/assets/images/download/result-praise-hd.jpg"
  };

  // 背景圖片映射
  const backgroundImages = {
    "身體": "/assets/images/fortune-slip-pink.jpg",
    "時間": "/assets/images/fortune-slip-orange.jpg",
    "禮物": "/assets/images/fortune-slip-purple.jpg",
    "服務": "/assets/images/fortune-slip-green.jpg",
    "讚美": "/assets/images/fortune-slip-gold.jpg"
  };

  // 初始動畫效果和播放結果音效
  useEffect(() => {
    console.log('結果類型:', result?.originalType);

    // 預加載圖片
    if (result?.originalType) {
      const bgImg = new Image();
      bgImg.src = backgroundImages[result.originalType];
      bgImg.onload = () => {
        if (backgroundImageRef.current) {
          backgroundImageRef.current.style.backgroundImage = `url(${backgroundImages[result.originalType]})`;
        }
      };

      // 預加載下載用的圖片
      const downloadImg = new Image();
      downloadImg.src = downloadResultImages[result.originalType];
    }

    // 播放結果音效
    SoundUtils.playResult();

    // 確保背景音樂恢復播放
    setTimeout(() => {
      if (window.resumeBackgroundMusic && typeof window.resumeBackgroundMusic === 'function') {
        window.resumeBackgroundMusic();
      }
    }, 500);

    // 背景轉換
    gsap.to(backgroundRef.current, {
      backgroundColor: 'transparent',
      duration: 0.5,
      ease: "power2.inOut"
    });

    // 內容淡入動畫
    gsap.fromTo(contentRef.current,
      {
        opacity: 0
      },
      {
        opacity: 1,
        duration: 0.5,
        ease: "power2.out"
      }
    );

    // 按鈕淡入動畫
    gsap.fromTo(buttonsRef.current,
      {
        opacity: 0,
        y: 20
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.5,
        delay: 0.3,
        ease: "power2.out"
      }
    );
  }, [result]);

  // 重新開始測驗
  const handleRestart = () => {
    // 播放按鈕音效
    SoundUtils.playButtonClick();
    window.location.reload();
  };

  // 社交媒體鏈接處理
  const handleSocialClick = (platform) => {
    // 播放按鈕音效
    SoundUtils.playButtonClick();

    // 根據平台打開不同的鏈接
    switch (platform) {
      case 'post':
        window.open('https://www.instagram.com/boooooom926', '_blank');
        break;
      case 'instagram':
        window.open('https://www.instagram.com/boooooom926', '_blank');
        break;
      case 'threads':
        window.open('https://www.threads.net/@boooooom926', '_blank');
        break;
      case 'form':
        window.open('https://docs.google.com/forms/d/e/1FAIpQLSdU-Cepx_q0G4JWyoJeD7LI2EKVsRYRFsxTEiBiNTqqfRbMwQ/viewform?fbclid=PAZXh0bgNhZW0CMTEAAac-IpSlBUlAg04yHPOX_Ig6tQla1f52oUr_Hdgx2iX-ru8vjKRbpFEM8Xuj9Q_aem_rNe3Qg3kl-WtHBEOGurY2A', '_blank');
        break;
      default:
        break;
    }
  };

  // 下載結果圖片
  const handleDownloadImage = () => {
    if (!result?.originalType) return;

    // 播放按鈕音效
    SoundUtils.playButtonClick();

    // 創建一個下載鏈接
    const link = document.createElement('a');
    link.href = downloadResultImages[result.originalType];
    link.download = `愛情語言-${result.type}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 分享給好友 - 優化 iOS 兼容性
  const handleShareWithFriends = async () => {
    // 播放按鈕音效
    SoundUtils.playButtonClick();

    // 準備分享內容
    const shareTitle = `我的愛情語言是「${result?.type || ''}」`;
    const shareText = `我測出來的愛情語言是「${result?.type || ''}」，快來看看你的是什麼！`;
    const shareUrl = window.location.href;

    // 檢查瀏覽器是否支持Web Share API
    if (navigator.share) {
      try {
        // 在 iOS 上簡化分享內容，只提供基本必要參數
        const shareData = {
          url: shareUrl
        };

        // 如果不是 iOS 設備，添加額外內容
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        if (!isIOS) {
          shareData.title = shareTitle;
          shareData.text = shareText;
        }

        // 嘗試分享
        await navigator.share(shareData);
        console.log('內容已成功分享');
      } catch (error) {
        console.error('分享時發生錯誤:', error);

        // 提供備用分享方式
        if (error.name === 'AbortError') {
          // 用戶取消了分享，不需要處理
        } else {
          // 分享失敗，提供備用方式
          fallbackShare(shareUrl, shareTitle);
        }
      }
    } else {
      // 瀏覽器不支持Web Share API，使用備用方式
      fallbackShare(shareUrl, shareTitle);
    }
  };

  // 備用分享方法
  const fallbackShare = (url, title) => {
    // 檢測是否在 iOS 上
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    if (isIOS) {
      // iOS 備用方式 - 使用自定義彈窗
      const tempInput = document.createElement('input');
      document.body.appendChild(tempInput);
      tempInput.value = url;
      tempInput.select();
      tempInput.setSelectionRange(0, 99999);
      document.execCommand('copy');
      document.body.removeChild(tempInput);

      alert('網址已複製到剪貼簿，你可以手動分享給朋友！');
    } else {
      // 其他平台備用方式
      alert('您的瀏覽器不支持分享功能，請手動複製網址分享');
    }
  };

  // 如果結果未加載，顯示加載提示
  if (!result) {
    return (
      <div className="result-page" style={{ backgroundColor: '#000', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>加載結果中...</div>
      </div>
    );
  }

  // 按鈕共用樣式
  const buttonBaseStyle = {
    width: '90%',
    maxWidth: '400px',
    padding: '12px 0',
    border: 'none',
    borderRadius: '50px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '15px'
  };

  return (
    <div className="result-page" ref={backgroundRef} style={{ backgroundColor: '#000' }}>
      {/* 背景圖片 */}
      <div
        ref={backgroundImageRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `url(${backgroundImages[result.originalType]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: 1
        }}
      ></div>

      <div ref={contentRef} style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* 結果圖片 - 直接頂部對齊，寬度100% */}
        <div style={{ width: '100%', marginBottom: '15px' }}>
          <img
            src={resultImages[result.originalType]}
            alt={`${result.type}`}
            style={{
              width: '100%',
              display: 'block'
            }}
            onError={(e) => {
              console.error('結果圖片加載失敗', e);
              e.target.style.background = '#f0f0f0';
              e.target.style.height = '300px';
              e.target.alt = '圖片加載失敗';
            }}
          />
        </div>

        {/* 按鈕區域 - 放在結果圖片下方 */}
        <div
          ref={buttonsRef}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            padding: '0 10px',
            marginBottom: '20px'
          }}
        >
          {/* 儲存結果圖片按鈕 - 使用淺色 */}
          <button
            onClick={handleDownloadImage}
            style={{
              ...buttonBaseStyle,
              backgroundColor: '#f5f5f5',
              color: '#333',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
            }}
          >
            儲存圖片
          </button>

          <button
            onClick={() => handleSocialClick('post')}
            style={{
              ...buttonBaseStyle,
              background: 'linear-gradient(135deg,rgb(129, 68, 72),rgb(136, 89, 121))',
              color: 'white',
              boxShadow: '0 4px 15px rgba(255, 107, 107, 0.3)'
            }}
          >
            看更多結果分析
          </button>

          {/* 分享給好友按鈕 */}
          <button
            onClick={handleShareWithFriends}
            style={{
              ...buttonBaseStyle,
              backgroundColor: '#06C755',
              color: 'white',
              boxShadow: '0 4px 15px rgba(66, 133, 244, 0.3)'
            }}
          >
            分享給朋友
          </button>

          {/* 重新測驗按鈕 */}
          <button
            onClick={handleRestart}
            style={{
              ...buttonBaseStyle,
              background: 'linear-gradient(135deg, var(--primary-red), #ff6b6b)',
              color: 'white',
              boxShadow: '0 4px 15px rgba(255, 107, 107, 0.3)'
            }}
          >
            再測一次
          </button>
        </div>

        {/* 關於我們卡片 */}
        <div
          className="glass-card"
          style={{
            margin: '10px auto 30px auto',
            padding: '20px',
            width: '90%',
            maxWidth: '600px',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderRadius: '20px',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <h2 style={{
            color: 'var(--primary-red)',
            marginBottom: '5px',
            fontSize: '1.3rem',
            textAlign: 'center'
          }}>
            都是謊言惹的禍
          </h2>

          <p style={{
            fontSize: '0.95rem',
            lineHeight: '1.5',
            marginBottom: '1px',
            textAlign: 'left',
          }}>
            我們是亞洲大學資訊傳播系大四的學生，畢業專題拍攝了這部愛情喜劇，故事在說宗翰、慧婷這對情侶被月老設下詛咒，宗翰只要一說謊，身邊的東西就會爆炸！一切都是由此展開的奇妙冒險。<br />
            還想了解更多嗎？趕快追蹤我們的社群，或是直接手刀報名首映會吧！謝謝你喜歡我們的作品！<br />
          </p>

          <div className="social-buttons" style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
            <button
              onClick={() => handleSocialClick('instagram')}
              className="social-button instagram"
            >
              Instagram
            </button>
            <button
              onClick={() => handleSocialClick('threads')}
              className="social-button threads"
            >
              Threads
            </button>
            <button
              onClick={() => handleSocialClick('form')}
              className="social-button form"
            >
              報名首映會
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultPage;